/**
 * "How to read a cell" — the key that sits above the KPI grid (DOP-609).
 *
 * It replaced an (i) popover. On the Summit call the analyst never found the
 * popover: instructions that appear only when you think to ask for them are
 * instructions a first-time reader does not get. So the key is on screen by
 * default, one line tall, and closing it is the user's decision ("Take the
 * tour" brings it back).
 *
 * Each sample is drawn with the grid's own classes (`kpiCellStates`), so what
 * the key teaches is exactly what the cells look like. The sample is a money
 * figure rather than "123": a bare integer beside a state name read as a COUNT
 * of cells in that state.
 */

import { Info, MousePointerClick, X } from "lucide-react";
import React from "react";

import {
	KPI_CELL_STATES,
	KPI_CELL_STATE_CLASSES,
	KPI_CELL_STATE_KEY_LABEL,
} from "./kpiCellStates";

const SAMPLE = "$1.84M";

export interface KpiCellGuideProps {
	onClose: () => void;
}

const KpiCellGuide: React.FC<KpiCellGuideProps> = ({ onClose }) => (
	<section
		aria-label="How to read a cell"
		data-tour="cell-guide"
		className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-2 rounded-[10px] border border-[#dcebea] bg-[#f6fbfb] px-3.5 py-2.5"
	>
		<span className="flex items-center gap-1.5 whitespace-nowrap text-sm font-semibold text-[#004C4B]">
			<Info className="h-[15px] w-[15px]" aria-hidden="true" />
			How to read a cell
		</span>
		{KPI_CELL_STATES.map((state) => (
			<span
				key={state}
				className="flex items-center gap-2 whitespace-nowrap text-[12.5px] text-[#3f4247]"
			>
				<span
					aria-hidden="true"
					className={`tabular-nums ${
						state === "empty" ? "font-semibold" : "font-medium"
					} ${KPI_CELL_STATE_CLASSES[state]}`}
				>
					{state === "empty" ? "—" : SAMPLE}
					{state === "overridden" && " •"}
				</span>
				{KPI_CELL_STATE_KEY_LABEL[state]}
			</span>
		))}
		<span className="ml-auto flex items-center gap-3 whitespace-nowrap">
			<span className="flex items-center gap-1.5 text-[12.5px] text-text-muted">
				<MousePointerClick className="h-3.5 w-3.5" aria-hidden="true" />
				Click any number to see its source
			</span>
			<button
				type="button"
				onClick={onClose}
				aria-label="Hide the cell guide"
				className="flex h-[26px] w-[26px] items-center justify-center rounded-md text-[#8a9099] hover:bg-white hover:text-text-base focus:outline-none focus:ring-1 focus:ring-[#008782]"
			>
				<X className="h-3.5 w-3.5" aria-hidden="true" />
			</button>
		</span>
	</section>
);

export default KpiCellGuide;
